import { readMyAccountAuth } from '@/utils/myaccount-auth-reader';
import countryData from '@/assets/data/countries.json';

type DynamicFormFieldUpdate = {
  value?: any;
  disabled?: boolean;
  visible?: boolean;
  required?: boolean;
  options?: Array<{ value: string; text: string }>;
};

type DynamicFormLike = {
  updateFields?: (updates: Record<string, DynamicFormFieldUpdate>) => void;
  getValues?: () => Record<string, any>;
};

const PROFILE_FIELD_ALIASES = {
  email: ['email', 'emailAddress'],
  firstName: ['firstName', 'firstname', 'first_name'],
  lastName: ['lastName', 'lastname', 'last_name'],
  companyName: [
    'institute',
    'companyName',
    'company',
    'organization',
    'organisation',
    'company_name',
  ],
};

const COUNTRY_FIELD_ALIASES = [
  'country',
  'Country',
  'Country / Region',
  'countryRegion',
  'countryCode',
  'country_region',
];
const STATE_FIELD_ALIASES = ['State', 'state'];
const ZIP_FIELD_ALIASES = ['zip_code', 'Zip Code', 'zipCode', 'zip', 'postalCode'];

const getStandardStateOptions = (countryCode: 'US' | 'CA' | 'AU') =>
  countryData.data.states[countryCode].map((state) => ({
    value: state.state_code,
    text: state.name,
  }));

const US_STATE_OPTIONS = getStandardStateOptions('US');
const CANADA_STATE_OPTIONS = getStandardStateOptions('CA');
const AUSTRALIA_STATE_OPTIONS = getStandardStateOptions('AU');
const COUNTRY_NAME_CODE_MAP: Record<string, string> = {
  AMERICA: 'US',
  AUSTRALIA: 'AU',
  CANADA: 'CA',
  CHINA: 'CN',
  PRC: 'CN',
  UK: 'GB',
  'UNITED KINGDOM': 'GB',
  'GREAT BRITAIN': 'GB',
  'UNITED STATES': 'US',
  'UNITED STATES OF AMERICA': 'US',
  USA: 'US',
  US: 'US',
};
const pickByAliases = (
  aliases: string[],
  ...sources: Array<Record<string, any> | null | undefined>
) => {
  for (const source of sources) {
    if (!source) continue;
    for (const key of aliases) {
      const value = source[key];
      if (value !== undefined && value !== null && `${value}`.trim()) return `${value}`.trim();
    }
  }
  return '';
};

const splitName = (value = '') => {
  const [firstName = '', ...rest] = value.trim().split(/\s+/);
  return { firstName, lastName: rest.join(' ') };
};

const getProfileUpdates = () => {
  const auth = readMyAccountAuth();
  if (!auth.isLoggedIn) return {};

  const profile = auth.profile || {};
  const user = auth.user || {};
  const fullName = pickByAliases(['name', 'fullName', 'userName'], profile, user);
  const nameParts = splitName(fullName);

  const values = {
    email: auth.email || pickByAliases(PROFILE_FIELD_ALIASES.email, profile, user),
    firstName: pickByAliases(PROFILE_FIELD_ALIASES.firstName, profile, user) || nameParts.firstName,
    lastName: pickByAliases(PROFILE_FIELD_ALIASES.lastName, profile, user) || nameParts.lastName,
    companyName: pickByAliases(PROFILE_FIELD_ALIASES.companyName, profile, user),
  };

  return Object.entries(values).reduce<Record<string, DynamicFormFieldUpdate>>(
    (updates, [field, value]) => {
      if (!value) return updates;
      for (const alias of PROFILE_FIELD_ALIASES[field as keyof typeof PROFILE_FIELD_ALIASES]) {
        updates[alias] = { value, disabled: true };
      }
      return updates;
    },
    {}
  );
};

const normalizeCountryCode = (value?: string) => {
  const code = `${value || ''}`.trim().toUpperCase();
  return COUNTRY_NAME_CODE_MAP[code] || code;
};

const getValueByAliases = (values: Record<string, any>, aliases: string[]) => {
  for (const alias of aliases) {
    const value = values[alias];
    if (value !== undefined && value !== null && `${value}`.trim()) return `${value}`.trim();
  }
  return '';
};

const getStateOptions = (countryCode: string) => {
  if (countryCode === 'US') return US_STATE_OPTIONS;
  if (countryCode === 'CA') return CANADA_STATE_OPTIONS;
  if (countryCode === 'AU') return AUSTRALIA_STATE_OPTIONS;
  return [];
};

const buildAliasUpdates = (aliases: string[], update: DynamicFormFieldUpdate) =>
  aliases.reduce<Record<string, DynamicFormFieldUpdate>>((result, alias) => {
    result[alias] = { ...update };
    return result;
  }, {});
export const updateRegionalFieldsByCountry = (
  form?: DynamicFormLike | null,
  countryValue?: string,
  stateValue?: string,
  resetState = false
) => {
  if (!form?.updateFields) return;

  const values = form.getValues?.() || {};
  const countryCode = normalizeCountryCode(
    countryValue || getValueByAliases(values, COUNTRY_FIELD_ALIASES)
  );
  const showState = countryCode === 'US' || countryCode === 'CA' || countryCode === 'AU';
  const showZip = countryCode === 'US';

  const stateUpdate: DynamicFormFieldUpdate = {
    visible: showState,
    required: showState,
    options: getStateOptions(countryCode),
  };
  if (stateValue !== undefined) {
    stateUpdate.value = stateValue;
  } else if (!showState || resetState) {
    stateUpdate.value = null;
  }

  const zipUpdate: DynamicFormFieldUpdate = {
    visible: showZip,
    required: showZip,
  };
  if (!showZip) zipUpdate.value = null;

  form.updateFields({
    ...buildAliasUpdates(STATE_FIELD_ALIASES, stateUpdate),
    ...buildAliasUpdates(ZIP_FIELD_ALIASES, zipUpdate),
  });
};
export const applyDynamicFormAutofill = async (form?: DynamicFormLike | null) => {
  if (!form?.updateFields) return;

  const updates = getProfileUpdates();

  form.updateFields(updates);
  updateRegionalFieldsByCountry(form);
};

export const isCountryField = (fieldName?: string) =>
  COUNTRY_FIELD_ALIASES.some((alias) => alias.toLowerCase() === fieldName?.toLowerCase());

export const isStateField = (fieldName?: string) =>
  STATE_FIELD_ALIASES.some((alias) => alias.toLowerCase() === fieldName?.toLowerCase());
